import React, {
  CSSProperties, PropsWithChildren, useEffect, useLayoutEffect, useMemo, useRef, forwardRef,
  useImperativeHandle,
  useState
} from 'react';
// import './index.css';

const defaultFrame = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
};

export default forwardRef((props: PropsWithChildren<{
  className?: string;
  style?: CSSProperties;
  defaultValue?: string;
  value?: string;
  type?: 'default' | 'numberPad' | 'decimalPad' | 'emailAddress' | 'password' | 'phonePad' | undefined;
  password?: boolean;
  placeholder?: string;
  disabled?: boolean;
  maxLength?: number;
  focus?: boolean;
  onClick?: (e?: any) => void;
  onInput?: (e?: any) => void;
  onChange?: (e?: any) => void;
  onFocus?: (e?: any) => void;
  onBlur?: (e?: any) => void;
  onConfirm?: (e?: any) => void;
  onKeyboardHeightChanged?: (e?: any) => void;
}>, ref) => {
  const {
    className,
    style,
    value,
    defaultValue,
    type,
    password,
    placeholder,
    disabled,
    maxLength,
    focus,
    onClick,
    onInput,
    onChange,
    onFocus,
    onBlur,
    onConfirm,
    onKeyboardHeightChanged,
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  const distCls = useMemo(() => {
    let _distCls = 'custom-mach-input';
    if (className) {
      _distCls = `${_distCls} ${className}`;
    }
    if (disabled) {
      _distCls = `${_distCls} custom-mach-input-disabled`;
    }
    return _distCls;
  }, [className, disabled]);
  const distType = useMemo(() => {
    if (password) return 'password';
    if (type === 'numberPad') {
      return 'number';
    }
    if (type === 'decimalPad') {
      return 'number';
    }
    return type;
  }, [password, type]);

  const inputModeAndPattern = useMemo(() => {
    if (type === 'numberPad') {
      return {
        inputMode: 'decimal' as const,
        pattern: '[0-9]*\\.?[0-9]*'
      };
    }
    if (type === 'decimalPad') {
      return {
        inputMode: 'decimal' as const,
        pattern: '[0-9]*\\.?[0-9]*'
      };
    }
    return {};
  }, [type]);

  const measure = () => {
    if (!inputRef.current) {
      return defaultFrame;
    }
    // 获取元素的边界矩形
    const rect = inputRef.current.getBoundingClientRect();

    // 获取父元素的边界矩形
    const parent = inputRef.current.parentElement;
    if (!parent) return;
    const parentRect = parent.getBoundingClientRect();

    // 计算相对父元素的坐标
    const x = rect.left - parentRect.left;
    const y = rect.top - parentRect.top;

    // 获取元素的宽度和高度
    const width = rect.width;
    const height = rect.height;
    // 返回结果
    return {
      x,
      y,
      width,
      height,
    };
  };

  useImperativeHandle(ref, () => ({
    ...inputRef.current,
    blur: () => {
      if (inputRef.current) {
        inputRef.current.blur();
      }
    },
    focus: () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },
    clear: () => {
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    isFocused: () => {
      if (inputRef.current) {
        return isFocused;
      }
      return false;
    },
    value: () => {
      if (inputRef.current) {
        return inputRef.current.value;
      }
      return '';
    },
    measureInView(target) {
      if (!target || !inputRef.current) return null;
      console.log('---measureInView---', inputRef.current, target);
      const rect = inputRef.current?.getBoundingClientRect();
      const targetRect = target?.getBoundingClientRect() || {
        top: 0,
        left: 0,
      };
      return {
        y: rect.top - targetRect.top,
        x: rect.left - targetRect.left,
        width: rect.width,
        height: rect.height,
      };
    },
    measure,
    setValue: (value) => {
      console.log('---setValue---', value);
      if (inputRef.current) {
        inputRef.current.value = value;
      }
    },
  }));

  useLayoutEffect(() => {
    if (!inputRef.current) return;
    const inputDom = inputRef.current;
    if (focus) {
      inputDom.focus();
    }
    function handler(e) {
      if (e?.keyCode !== 13) return;
      onConfirm && onConfirm({
        detail: e.target.value,
        type: 'confirm',
        bubbles: false,
        cancelable: false,
        timeStamp: Date.now(),
        e,
      });
      e?.target?.blur && e.target.blur();
    }
    if (inputDom) {
      inputDom.addEventListener('keydown', handler);
    }
    // eslint-disable-next-line
        return () => {
      if (!inputDom) return;
      inputDom.removeEventListener('keydown', handler);
    };
  }, [focus, onConfirm]);
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = defaultValue || '';
    }
  }, [defaultValue]);
  function handleInput(e) {
    const value = e?.target?.value;
    const timeStamp = Date.now();
    const type = 'change';

    onInput && onInput({
      detail: value,
      type,
      bubbles: false,
      cancelable: false,
      timeStamp,
      e,
    });
    onChange && onChange({
      detail: value,
      type,
      bubbles: false,
      cancelable: false,
      timeStamp,
      e,
    });
  }
  function handleFocus(e) {
    setIsFocused(true);
    onFocus && onFocus(e);
  }
  function handleBlur(e) {
    setIsFocused(false);
    onBlur && onBlur(e);
  }
  return (
    <input
      value={value}
      // @ts-ignore
      type={distType}
      {...inputModeAndPattern}
      placeholder={placeholder}
      disabled={disabled}
      className={distCls}
      maxLength={maxLength === -1 ? undefined : maxLength}
      style={style}
      inner
      ref={inputRef}
      onClick={onClick}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onInput={handleInput}
      // onKeyboardHeightChanged={onKeyboardHeightChanged}
    />
  );
});
